import React, { useEffect, useState } from "react";
import { Alert, Button, Checkbox, colors, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid2 as Grid, Radio, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import PdfView, { jsPDFPaperSizes, paperSizes } from "./pdfView";
import { SelectBoxOption } from "../../interfaces";
import { createOption } from "../keywordSearch";
import { CheckListData } from "../../screens/viewCheckList/viewCheckListDetail";
import { useGetDocumentTemplatesDDOptionsQuery } from "../../redux/services";
import { TemplateResponse } from "../../redux/slices/documentTemplate";
import { orientationOptions } from "../../screens/documentTemplates/dashboard/addEditTemplate";
import { HighlightSelectBox } from "../highLightSelectBox";
import { SectionPaper } from "../sectionPaper";
import { Costing, IBasicDetails, Orientation, PDFConfigState } from "pdf-generator";
import ExcelGenerator from "../../screens/viewPricing/Preview/excelGenerator";
import { closeDialog, openDialog } from "../../redux/slices/dialog";
import { useAppDispatch } from "../../redux";
import { matchIsValidColor, MuiColorInput } from "mui-color-input";
import { Download } from "@mui/icons-material";
import { BOMLevelType, PricingPDFConfig, ReportType } from "../pdfBuilder/types";
import { levelOptions, reportTypeOptions } from "../../redux/slices/pricing";
// import { AntSwitch } from "../antSwitch";

const renderingOptions = [createOption("No Template"), createOption("With Template")];

const CostConfiguration: React.FC<{
    configState: PDFConfigState
    onApply: (data: PDFConfigState) => void
}> = ({ configState, onApply }) => {
    const dispatch = useAppDispatch();

    const [levelLimit, setLimit] = React.useState<SelectBoxOption>(levelOptions[0]);
    const [reportType, setReportType] = React.useState<SelectBoxOption>(reportTypeOptions[2]);
    const [hasOtherCost, setHasOtherCost] = React.useState(false);
    const [hasAdjustment, setHasAdjustment] = React.useState(false);
    const [hasTax, setHasTax] = React.useState(false);
    const [onlyBOM, setOnlyBOM] = React.useState(true);
    const [hasNotes, setHasNotes] = React.useState(false);
    const [tableHeaderColor, setHeaderColor] = React.useState('#d1c4e9');
    const [useMBFormatter, setMBFormatter] = React.useState(false);
    const [isNormalized, setIsNormalized] = React.useState(false);
    const [hasCostGroupItems, setHasCGI] = React.useState(false);

    const noBreakup = levelLimit?.value === "Nobreakup";

    useEffect(() => {
        setLimit(configState.levelLimit)
        setReportType(configState.reportType)
        setHasOtherCost(configState.hasOtherCost)
        setHasAdjustment(configState.hasAdjustment)
        setHasTax(configState.hasTax)
        setOnlyBOM(configState.onlyBOM)
        setHasNotes(configState.hasNotes)
        setMBFormatter(configState.useMBFormatter)
        setIsNormalized(configState.isNormalized)
        setHasCGI(configState.hasCostGroupItems)
        if (matchIsValidColor(configState.tableHeaderColor)) {
            setHeaderColor(configState.tableHeaderColor)
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (levelLimit?.value === "Nobreakup") {
            setHasOtherCost(false)
            setHasAdjustment(false)
            setHasTax(true)
            setOnlyBOM(false);
        }
    }, [levelLimit?.value]);

    useEffect(() => {
        if (isNormalized) {
            setLimit(levelOptions[0]);
            setOnlyBOM(true);
            setHasOtherCost(false)
            setHasAdjustment(false)
            setHasTax(false);
            setReportType(reportTypeOptions[0]);
            setHasCGI(false);
        }
    }, [isNormalized]);

    return (<>
        <DialogTitle>
            <Typography
                variant="h6"
                color="textPrimary"
                sx={{
                    width: "100%",
                    fontFamily: 'htrts_medium',
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
            >
                PDF Configuration
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Stack spacing={3} mt={1}>
                {/* Level Limit for BOM Tree */}
                <HighlightSelectBox
                    disableClearable
                    label={"BOM Level"}
                    margin={"none"}
                    value={levelLimit}
                    options={levelOptions}
                    onChange={(option: SelectBoxOption) => { setLimit(option as any) }}
                    disabled={isNormalized}
                />

                {/* Report Type */}
                <HighlightSelectBox
                    disableClearable
                    label={"Report Type"}
                    margin={"none"}
                    value={reportType}
                    options={reportTypeOptions}
                    onChange={(option: SelectBoxOption) => { setReportType(option as any) }}
                    disabled={isNormalized}
                />

                {/* Color Picker */}
                <MuiColorInput
                    label={"Table Header Color"}
                    size={"small"}
                    fallbackValue={"#d1c4e9"}
                    value={tableHeaderColor}
                    onChange={(color: string) => {
                        if (matchIsValidColor(color)) {
                            setHeaderColor(color)
                        }
                    }}
                />

                <Grid container spacing={1}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hasOtherCost}
                                    onChange={() => {
                                        setHasOtherCost(!hasOtherCost)
                                    }}
                                    disabled={noBreakup || isNormalized}
                                />
                            }
                            label={"Include Other cost"}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hasAdjustment}
                                    onChange={() => {
                                        setHasAdjustment(!hasAdjustment)
                                    }}
                                    disabled={noBreakup || isNormalized}
                                />
                            }
                            label={"Include Adjustments"}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hasTax}
                                    onChange={() => {
                                        setHasTax(!hasTax)
                                    }}
                                    disabled={isNormalized}
                                />
                            }
                            label={"Include Tax"}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={onlyBOM}
                                    onChange={() => {
                                        setOnlyBOM(!onlyBOM)
                                    }}
                                    disabled={noBreakup || isNormalized}
                                />
                            }
                            label={"Only BOM in Major Item"}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hasNotes}
                                    onChange={() => {
                                        setHasNotes(!hasNotes)
                                    }}
                                    disabled={noBreakup}
                                />
                            }
                            label={"Include Notes"}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isNormalized}
                                    onChange={() => {
                                        setIsNormalized(!isNormalized)
                                    }}
                                />
                            }
                            label={"Normalize Cost"}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hasCostGroupItems}
                                    onChange={() => {
                                        setHasCGI(!hasCostGroupItems)
                                    }}
                                    disabled={isNormalized}
                                />
                            }
                            label={"Include Cost Group Items"}
                        />
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12
                        }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useMBFormatter}
                                    onChange={() => {
                                        setMBFormatter(!useMBFormatter)
                                    }}
                                />
                            }
                            label={"Include Millions & Billions Formatting"}
                        />
                    </Grid>
                </Grid>
            </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center !important', mb: 2 }}>
            <Stack sx={{ flexDirection: { xs: "column", md: "row", alignItems: "center", justifyContent: "center", gap: 10 } }}>
                <Button
                    variant="outlined"
                    onClick={() => dispatch(closeDialog())}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        onApply({
                            levelLimit,
                            reportType,
                            hasOtherCost,
                            hasAdjustment,
                            hasTax,
                            onlyBOM,
                            hasNotes,
                            tableHeaderColor,
                            useMBFormatter,
                            isNormalized,
                            hasCostGroupItems
                        });
                        dispatch(closeDialog());
                    }}
                >
                    Apply Config
                </Button>
            </Stack>
        </DialogActions>
    </>);
};

const DataSheetPDFPreview = ({
    basicDetails, dataSheet, title = '', type = 'datasheet',
    headerTitle = '', selectedSize, selectedOrientation, pricingID = '', footerURL = '', pdfConfig, released = false
}: {
    basicDetails?: IBasicDetails | Costing; dataSheet?: CheckListData; title?: string; type?: 'datasheet' | 'costbreakupreport'
    headerTitle?: string, selectedSize?: SelectBoxOption, selectedOrientation?: SelectBoxOption, pricingID?: string, footerURL?: string, pdfConfig?: PricingPDFConfig, released?: boolean
}) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    const templateOptions = useGetDocumentTemplatesDDOptionsQuery({}, { refetchOnFocus: true, refetchOnMountOrArgChange: true, });

    const [orientation, setOrientation] = React.useState<SelectBoxOption | null>(selectedOrientation ?? orientationOptions[0])
    const [paperSize, setPaperSize] = React.useState<SelectBoxOption | null>(selectedSize ?? paperSizes[6]);
    const [renderingType, setRenderingType] = React.useState(renderingOptions[0]?.value);
    const [selectedTemplate, setTemplate] = React.useState<Partial<TemplateResponse & SelectBoxOption> | null>(null);
    const [configState, setConfigState] = useState<PDFConfigState>({
        levelLimit: levelOptions[0],
        reportType: reportTypeOptions[2],
        hasOtherCost: false,
        hasAdjustment: false,
        hasTax: false,
        onlyBOM: true,
        hasNotes: true,
        tableHeaderColor: '#d1c4e9',
        useMBFormatter: true,
        isNormalized: false,
        hasCostGroupItems: false
    });
    const [pdfInfo, setPdfInfo] = React.useState('');

    const options = templateOptions?.data ?? [];

    const current_paper_size = (selectedTemplate?.paper_size ?? paperSize?.value ?? "A4") as string;
    const current_orientation = (selectedTemplate?.orientation ?? orientation?.value ?? "portrait") as Orientation;

    const paperSizeOptions = jsPDFPaperSizes.filter(format => !(["A10", "B10", "C10"].includes(format.label)));

    const handleConfig = () => {
        dispatch(
            openDialog({
                title: "",
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                enablePadding: false,
                body: <CostConfiguration configState={configState} onApply={(data) => {
                    setConfigState(data);
                    pdfConfig?.onReportConfigChange({
                        report_type: data.reportType.value as ReportType,
                        bom_level: data.levelLimit.value as BOMLevelType,
                        include_millions_formatting: data.useMBFormatter ?? false,
                        include_adjustments: data.hasAdjustment ?? false,
                        include_notes: data.hasNotes ?? false,
                        include_other_costs: data.hasOtherCost ?? false,
                        include_tax: data.hasTax ?? false,
                        normalize_cost: data.isNormalized ?? false,
                        only_bom: data.onlyBOM ?? false,
                        table_header_color: data.tableHeaderColor ?? "#d1c4e9"
                    })
                }} />,
            })
        );
    }

    const handleDownload = (url: string, title: string) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = `${title}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }

    const downloadAction = <Button variant="contained" onClick={() => handleDownload(pdfInfo, title)} startIcon={<Download />}>PDF</Button>

    React.useEffect(() => {
        const report_config = pdfConfig?.report_config;
        if (report_config) {
            const obj: Record<string, string | boolean | SelectBoxOption> = {};
            if (report_config.table_header_color) {
                obj.tableHeaderColor = report_config?.table_header_color || "#d1c4e9";
            }

            if (report_config.hasOwnProperty('include_millions_formatting')) {
                obj.useMBFormatter = report_config?.include_millions_formatting || false;
            }
            
            if (report_config.hasOwnProperty('include_adjustments')) {
                obj.hasAdjustment = report_config?.include_adjustments || false;
            }

            if (report_config.hasOwnProperty('include_notes')) {
                obj.hasNotes = report_config?.include_notes || false;
            }

            if (report_config.hasOwnProperty('include_other_costs')) {
                obj.hasOtherCost = report_config?.include_other_costs || false;
            }

            if (report_config.hasOwnProperty('include_tax')) {
                obj.hasTax = report_config?.include_tax || false;
            }

            if (report_config.hasOwnProperty('normalize_cost')) {
                obj.isNormalized = report_config?.normalize_cost || false;
            }

            if (report_config.hasOwnProperty('only_bom')) {
                obj.onlyBOM = report_config?.only_bom || false;
            }

            if (report_config.hasOwnProperty('report_type')) {
                obj.reportType = reportTypeOptions.find(l => l.value === report_config?.report_type) || reportTypeOptions[2];
            }

            if (report_config.hasOwnProperty('bom_level')) {
                obj.levelLimit = levelOptions.find(l => l.value === report_config?.bom_level) || levelOptions[0];
            }

            setConfigState((prevState) => ({ ...prevState, ...obj }))
        }
    }, [pdfConfig])

    return (
        <Stack sx={{ display: "flex", height: "100%", background: (theme) => theme.palette.background.default }}>
            <Stack width={"100%"} height={"100%"}>
                <Stack spacing={2} mb={1}>
                    <SectionPaper stack title="Configure Preview" renderButtons={type === "costbreakupreport" ? [
                        <Stack alignItems={"center"} gap={2} sx={{ flexDirection: { xs: 'column', md: "row" } }}>
                            {/* PDF Config  */}
                            {!released &&
                                <Button variant={"outlined"} onClick={handleConfig}>Apply Config</Button>
                            }
                            {/* Excel Generator */}
                            <ExcelGenerator data={basicDetails as any} pricingID={pricingID} levelLimit={Infinity} />
                            {/* Download PDF */}
                            {downloadAction}
                        </Stack>
                    ] : [downloadAction]}>
                        <Grid container spacing={1} width={"100%"} columns={13}>
                            <Grid
                                size={{
                                    xs: 13,
                                    md: 6
                                }}>
                                <Stack spacing={2}>
                                    <FormControlLabel
                                        disabled={released}
                                        value={renderingOptions[0].value}
                                        control={
                                            <Radio
                                                size="small"
                                                checked={renderingOptions[0]?.value === renderingType}
                                                onChange={() => {
                                                    setRenderingType(renderingOptions[0].value);
                                                    setTemplate(null);
                                                    setPaperSize(paperSizes[6]);
                                                    setOrientation(orientationOptions[0]);
                                                }}
                                                value={renderingOptions[0].value} />
                                        }
                                        label={renderingOptions[0].label}
                                    />
                                    <HighlightSelectBox
                                        disabled={renderingOptions[1]?.value === renderingType || released}
                                        label={"Paper Size"}
                                        margin={"none"}
                                        fullWdith
                                        value={paperSize}
                                        options={paperSizeOptions}
                                        onChange={(option: SelectBoxOption) => { setPaperSize(option) }}
                                    />
                                    <HighlightSelectBox
                                        disabled={renderingOptions[1]?.value === renderingType || released}
                                        label={"Orientation"}
                                        margin={"none"}
                                        fullWdith
                                        value={orientation}
                                        options={orientationOptions}
                                        onChange={(option: SelectBoxOption) => { setOrientation(option) }}
                                    />
                                </Stack>
                            </Grid>
                            <Grid
                                mt={2}
                                size={{
                                    xs: 13,
                                    md: 1
                                }}>
                                <Stack sx={{ width: "100%", height: "100%" }} justifyContent={matches ? "center" : "flex-start"} alignItems={matches ? "stretch" : 'center'}><Divider orientation={matches ? "horizontal" : "vertical"} /></Stack>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 13,
                                    md: 6
                                }}>
                                <Stack spacing={2}>
                                    <FormControlLabel
                                        disabled={released}
                                        value={renderingOptions[1].value}
                                        control={
                                            <Radio
                                                size="small"
                                                checked={renderingOptions[1].value === renderingType}
                                                onChange={() => {
                                                    setRenderingType(renderingOptions[1].value);
                                                    setOrientation(null);
                                                    setPaperSize(null);
                                                }}
                                                value={renderingOptions[1].value}
                                                name="radio-buttons"
                                            />
                                        }
                                        label={renderingOptions[1].label}
                                    />

                                    <HighlightSelectBox
                                        disabled={renderingOptions[0]?.value === renderingType || released}
                                        label={"Select template"}
                                        margin={"none"}
                                        fullWdith
                                        value={selectedTemplate as SelectBoxOption}
                                        options={options}
                                        onChange={(option: SelectBoxOption) => { setTemplate(option) }}
                                    />
                                    <Alert variant="outlined" severity="warning" sx={{ borderWidth: 6, borderRight: 0, borderTop: 0, borderBottom: 0, mt: 2 }}>
                                        <Typography variant="caption" color={colors.grey[500]}>
                                            Caution! Please ensure the table width is not exceeding the selected template page width!
                                            Please select appropriate template to accommodate table content.
                                        </Typography>
                                    </Alert>
                                </Stack>
                            </Grid>
                        </Grid>
                    </SectionPaper>

                    {/* {type === 'costbreakupreport' && <SectionPaper title="Report Configuration">
                        <Grid container spacing={1} width={"100%"}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label={"Markup (%)"}
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={costMargin}
                                    onChange={(e) => setCostMargin(parseFloat(e.target.value))}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </SectionPaper>} */}
                </Stack>

                {/* PDF View */}
                <PdfView
                    headerTitle={headerTitle}
                    title={title}
                    basicDetails={basicDetails}
                    dataSheet={dataSheet}
                    templateData={selectedTemplate}
                    orientation={current_orientation}
                    paperSize={current_paper_size}
                    type={type}
                    pricingID={pricingID}
                    configState={configState}
                    footerURL={footerURL}
                    pdfDetails={{
                        pdfInfo,
                        setPdfInfo
                    }}
                />
            </Stack>
        </Stack>
    );
};

export { DataSheetPDFPreview };